import React, { useEffect, useRef } from "react";

import logo from "../../Assets/logo.png";
import language from "../../localization";
import styles from "./style.module.css";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const getDirection = (dir) => {
  if (dir) return dir;
  const currentLanguage = language.getLanguage?.() || "en";
  return language.getLanguageDirection?.(currentLanguage) || language.textDirection || "ltr";
};

function LoaderNetworkCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }

    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d", { alpha: true });
    if (!canvas || !context) return undefined;

    let animationFrame = 0;
    let width = 0;
    let height = 0;
    let points = [];
    let isRunning = true;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      const ratio = Math.min(window.devicePixelRatio || 1, 1.35);

      canvas.width = Math.floor(width * ratio);
      canvas.height = Math.floor(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);

      const count = Math.max(18, Math.min(34, Math.round((width * height) / 42000)));
      points = Array.from({ length: count }, (_, index) => {
        const angle = index * 2.39996;
        const ring = 0.24 + (index % 8) * 0.075;
        return {
          x: ((Math.cos(angle) * ring + 1) / 2) * width,
          y: ((Math.sin(angle) * ring + 1) / 2) * height,
          vx: (((index * 7) % 9) - 4) * 0.035,
          vy: (((index * 5) % 9) - 4) * 0.03,
        };
      });
    };

    const draw = () => {
      if (!isRunning) return;

      context.clearRect(0, 0, width, height);

      for (let index = 0; index < points.length; index += 1) {
        const point = points[index];
        point.x += point.vx;
        point.y += point.vy;

        if (point.x < 0 || point.x > width) point.vx *= -1;
        if (point.y < 0 || point.y > height) point.vy *= -1;

        for (let nextIndex = index + 1; nextIndex < points.length; nextIndex += 1) {
          const other = points[nextIndex];
          const dx = point.x - other.x;
          const dy = point.y - other.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            context.globalAlpha = Math.max(0, 1 - distance / 150) * 0.54;
            context.strokeStyle = "rgba(154, 211, 234, 0.24)";
            context.lineWidth = 1;
            context.beginPath();
            context.moveTo(point.x, point.y);
            context.lineTo(other.x, other.y);
            context.stroke();
          }
        }

        context.globalAlpha = 0.72;
        context.fillStyle = "rgba(255, 255, 255, 0.68)";
        context.beginPath();
        context.arc(point.x, point.y, 1.35, 0, Math.PI * 2);
        context.fill();
      }

      context.globalAlpha = 1;
      animationFrame = window.requestAnimationFrame(draw);
    };

    const handleVisibility = () => {
      if (document.hidden) {
        window.cancelAnimationFrame(animationFrame);
        return;
      }
      animationFrame = window.requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      isRunning = false;
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  return <canvas className={styles.network} ref={canvasRef} aria-hidden="true" />;
}

const UniversityLoader = ({ className, exiting = false, message, dir }) => {
  const direction = getDirection(dir);

  return (
    <div
      className={cx(styles.screen, exiting && styles.exiting, className)}
      dir={direction}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <LoaderNetworkCanvas />
      <div className={styles.gridTexture} aria-hidden="true" />
      <div className={styles.content}>
        <span className={styles.logoMark} aria-hidden="true">
          <img src={logo} alt="" />
        </span>
        <div className={styles.copy}>
          <span>{language.saba_university || "Saba University"}</span>
          <h1>{message || language.loading_website || language.loading || "Loading website..."}</h1>
          <p>{language.please_wait || "Please wait..."}</p>
        </div>
        <div className={styles.progressTrack} aria-hidden="true">
          <span />
        </div>
      </div>
    </div>
  );
};

export default UniversityLoader;